import { useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { Button, Flex, Segmented, Skeleton, Text } from '@nula/core-ui';
import { useCanvasPreviewRenderer } from '@/lib/canvas/canvas-preview-context';
import { applyProposal, type Proposal } from '@/lib/templates/api';
import {
  CONFLICT_SENTENCE,
  PEEK_HINT,
  REVIEW_LABELS,
  UNCERTAIN_APPLY_LINE,
  applyFailure,
  reviewTitle,
} from '@/lib/templates/review-copy';
import { reportReviewFailure } from '@/lib/templates/report-review-failure';
import type { FlipView } from '../hooks/use-flip-view';
import styles from './surface-review-diagram.module.css';

interface SurfaceReviewDiagramBarProps {
  entityId: string;
  surfaceId: string;
  proposal: Proposal;
  templateName: string | null;
  view: FlipView;
  onViewChange: (view: FlipView) => void;
  /** `settled` is true when the proposal itself closed, false when the person only left review. */
  onClosed: (settled: boolean) => void;
  onReplaced: () => void;
}

export function SurfaceReviewDiagramBar({
  entityId,
  surfaceId,
  proposal,
  templateName,
  view,
  onViewChange,
  onClosed,
  onReplaced,
}: SurfaceReviewDiagramBarProps) {
  const barRef = useRef<HTMLElement>(null);
  const [busy, setBusy] = useState(false);
  const [conflicted, setConflicted] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    barRef.current?.focus();
  }, []);

  const decide = async (accept: boolean, force: boolean) => {
    setBusy(true);
    setMessage(null);
    try {
      const result = await applyProposal(entityId, surfaceId, {
        proposalId: proposal.proposalId,
        accept,
        ...(force ? { force: true } : {}),
      });
      if (result.state === 'closed') {
        onClosed(true);
        return;
      }
      setConflicted(result.sections.some((section) => section.outcome === 'conflicted'));
      setBusy(false);
    } catch (error) {
      reportReviewFailure(error, 'diagram apply');
      const failure = applyFailure(error);
      if (failure === 'uncertain') {
        setMessage(UNCERTAIN_APPLY_LINE);
        setBusy(false);
        onReplaced();
        return;
      }
      if (failure === 'closed' || failure === 'removed') {
        onClosed(true);
        return;
      }
      if (failure === 'replaced') {
        onReplaced();
        return;
      }
      setMessage(failure.message);
      setBusy(false);
    }
  };

  const viewOptions = [
    { label: REVIEW_LABELS.viewCurrent, value: 'current' },
    { label: REVIEW_LABELS.viewNew, value: 'new' },
  ];
  const chooseView = (value: string) => {
    onViewChange(value === 'current' ? 'current' : 'new');
    barRef.current?.focus();
  };

  return (
    <Flex className={styles.column}>
      <Flex
        ref={barRef}
        role="group"
        aria-label={REVIEW_LABELS.reviewing}
        tabIndex={-1}
        aria-keyshortcuts="Space"
        className={clsx(styles.bar, conflicted && styles.conflicted)}
      >
        <Flex vertical className={styles.message}>
          {conflicted ? (
            <Text size="sm" className={styles.conflictText}>
              {CONFLICT_SENTENCE}
            </Text>
          ) : (
            <Text size="sm">{reviewTitle(templateName)}</Text>
          )}
          {message && (
            <Text role="alert" type="secondary" size="sm">
              {message}
            </Text>
          )}
        </Flex>
        <Flex className={styles.actions}>
          <Button size="sm" variant="text" className={styles.touch} disabled={busy} onClick={() => onClosed(false)}>
            {REVIEW_LABELS.close}
          </Button>
          <Button size="sm" variant="default" className={styles.touch} disabled={busy} onClick={() => void decide(false, false)}>
            {REVIEW_LABELS.keepCurrent}
          </Button>
          <Button size="sm" variant="primary" className={styles.touch} disabled={busy} onClick={() => void decide(true, conflicted)}>
            {conflicted ? REVIEW_LABELS.useNewAnyway : REVIEW_LABELS.useNew}
          </Button>
        </Flex>
        <Flex className={styles.view}>
          <Segmented
            ghost={false}
            size="sm"
            role="group"
            aria-label={REVIEW_LABELS.versionShown}
            className={styles.switch}
            options={viewOptions}
            value={view}
            disabled={busy}
            onChange={chooseView}
          />
          <Text type="secondary" size="sm" className={styles.hint}>
            {PEEK_HINT}
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
}

export function SurfaceReviewDiagramProposal({
  entityId,
  surfaceId,
  concealed,
}: {
  entityId: string;
  surfaceId: string;
  concealed: boolean;
}) {
  const CanvasPreview = useCanvasPreviewRenderer();
  const nestedKey = `proposal_${surfaceId}`;
  return (
    <Flex data-review-canvas data-concealed={String(concealed)} className={styles.proposalColumn}>
      {CanvasPreview ? (
        <CanvasPreview
          key={nestedKey}
          entityId={entityId}
          entityKindHint="document"
          nested
          nestedKey={nestedKey}
          readOnly
        />
      ) : (
        <Skeleton />
      )}
    </Flex>
  );
}
